import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	type ExtensionManifest,
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import { DEFAULT_ALIGNMENT, setParameters } from '@atlaskit/native-embeds-common';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core';
import { DBExperienceManifest } from '@atlassian/native-embeds-db-experience';
import { MauiExperienceManifest } from '@atlassian/native-embeds-maui-experience';
import { PageExperienceManifest } from '@atlassian/native-embeds-page-experience';
import { WhiteboardExperienceManifest } from '@atlassian/native-embeds-whiteboard-experience';

// Register all experience manifests at module-load time.
// This is intentionally module-level (not inside a React lifecycle) because the
// facade must be available before any child embed loads.
getNativeEmbedFacade().setup({
	experiences: [
		WhiteboardExperienceManifest,
		DBExperienceManifest,
		MauiExperienceManifest,
		PageExperienceManifest,
	],
});

const buildNativeEmbedParameters = (url: string) =>
	setParameters(
		{},
		{
			url,
			alwaysShowTitle: false,
			alignment: DEFAULT_ALIGNMENT,
			height: 600,
			width: undefined,
		},
	);

const manifest: ExtensionManifest = {
	title: 'Native Embed',
	type: NATIVE_EMBED_EXTENSION_TYPE,
	key: NATIVE_EMBED_EXTENSION_KEY,
	description: 'Embed native Atlassian content',
	modules: {
		nodes: {
			default: {
				type: 'extension',
				render: () =>
					import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui'),
				// @ts-expect-error This option is experimental and not reflected in the manifest type definition
				__hideFrame: true,
			},
			...Object.fromEntries(
				getNativeEmbedFacade()
					.getAllExperienceIds()
					.map((experienceId) => [
						experienceId,
						{
							type: 'extension',
							render: () =>
								import(/* webpackChunkName: "@atlassian/native-embeds-editor-extension" */ './ui'),
							__hideFrame: true,
						},
					]),
			),
		},
		autoConvert: {
			url: [
				(url: string): ADFEntity | undefined => {
					// Check if URL matches any registered experience patterns
					const experienceId = getNativeEmbedFacade().getExperienceIdFromUrl(url);
					if (experienceId) {
						// Check if this experience should auto-convert to native embed
						const manifest = getNativeEmbedFacade().getManifestByUrl(url);
						if (manifest && manifest.shouldAutoConvertToNativeEmbed === false) {
							// Return undefined to allow smart link handling instead
							return undefined;
						}

						return {
							type: 'extension',
							attrs: {
								extensionKey: `${NATIVE_EMBED_EXTENSION_KEY}:${experienceId}`,
								extensionType: NATIVE_EMBED_EXTENSION_TYPE,
								parameters: buildNativeEmbedParameters(url),
							},
						};
					}

					// Return undefined if URL doesn't match - handled by smart links instead.
					return undefined;
				},
			],
		},
	},
};

export default manifest;
